import React, { useState, useEffect } from 'react';
import {
  Stack, Typography, Button, Select, MenuItem,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Controller } from 'react-hook-form';
import { useStore } from '@mcn-platform/models';
import ReferenceSelector from './ReferenceSelector';
import { StandardFieldProps } from './FieldTypes';

interface IPrerequisiteItem {
  prerequisiteType: string;
  courseKey: string;
  certificationID: string;
  microcastKey: string;
  onDelete: () => void;
}

const PrerequisiteItem = ({
  prerequisiteType, courseKey, certificationID, microcastKey, onDelete,
}: IPrerequisiteItem) => {
  const [name, setName] = useState<string>('Loading...');
  const store = useStore();

  const isCourse = prerequisiteType === 'course';

  useEffect(() => {
    const fetchName = async () => {
      try {
        if (prerequisiteType && (courseKey || microcastKey)) {
          const entity = await store.load(prerequisiteType, isCourse ? courseKey : microcastKey);

          if (isCourse && certificationID) {
            const certSections = entity?.getCertificationSections() || [];

            const certificationName = certSections
              .find((section) => section.item?.certification === certificationID)?.name;

            setName(
              `Course: ${entity?.name}${
                certificationName ? `, Certification: ${certificationName}` : ''
              }`,
            );
          } else if (prerequisiteType === 'microcast') {
            setName(`Microcast: ${entity?.name || microcastKey}`);
          } else {
            setName(`Unknown: ${courseKey || microcastKey}`);
          }
        }
      } catch (error) {
        console.error(`Error loading prerequisite (${prerequisiteType}):`, error);
        setName('Error loading prerequisite');
      }
    };

    fetchName();
  }, [prerequisiteType, courseKey, microcastKey, store, isCourse, certificationID]);

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        borderBottom: '1px solid #ddd',
        paddingBottom: 1,
        paddingTop: 1,
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
        {name}
      </Typography>
      <Button size="small" color="secondary" onClick={onDelete}>
        Delete
      </Button>
    </Stack>
  );
};

const CoursePrerequisiteField = ({
  control,
  fieldName,
}: StandardFieldProps) => {
  const [certifications, setCertifications] = useState<Array<{ value: string; label: string }>>([]);

  const [localValue, setLocalValue] = useState({
    prerequisiteType: 'course',
    courseKey: '',
    microcastKey: '',
    certificationID: '',
  });
  const store = useStore();

  const isCourse = localValue?.prerequisiteType === 'course';

  useEffect(() => {
    const fetchCertifications = async () => {
      if (isCourse && localValue.courseKey) {
        const course = await store.load('course', localValue.courseKey);
        const availableCertifications = course?.getCertificationSections()?.map((section) => ({
          value: section.item?.certification?.key,
          label: section.name || `Certification ${section.item?.certification?.key}`,
        })) || [];
        setCertifications(availableCertifications);
      } else {
        setCertifications([]);
      }
    };

    fetchCertifications();
  }, [localValue.courseKey, isCourse, store]);

  const canAdd = localValue.prerequisiteType
    && (isCourse ? localValue.courseKey && localValue.certificationID : localValue.microcastKey);

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { value, onChange } }) => {
        const handleAdd = () => {
          if (!canAdd) return;

          const newEntry = {
            prerequisiteType: localValue.prerequisiteType,
            course: isCourse ? localValue.courseKey : undefined,
            microcast: !isCourse ? localValue.microcastKey : undefined,
            certificationID: localValue.certificationID || undefined,
          };

          const updatedValue = Array.isArray(value) ? [...value, newEntry] : [newEntry];

          onChange(updatedValue);
          setLocalValue((prevValue) => ({
             ...prevValue,
            courseKey: '',
            microcastKey: '',
            certificationID: '',
          }));
        };

        const handleDelete = (index: number) => {
          const newArray = [...value];
          newArray.splice(index, 1);
          onChange(newArray);
        };

        return (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack flex={1}>
                <Select
                  value={localValue.prerequisiteType || ''}
                  onChange={(id) => {
                    setLocalValue({
                      ...localValue,
                      prerequisiteType: id.target.value,
                      certificationID: '',
                    });
                  }}
                >
                  <MenuItem value="course">Course</MenuItem>
                  <MenuItem value="microcast">Microcast</MenuItem>
                </Select>
              </Stack>
              <Stack flex={2}>
                <ReferenceSelector
                  key="referenceSelector"
                  modelName={isCourse ? 'course' : 'microcast'}
                  id={isCourse ? 'course' : 'microcast'}
                  value={isCourse ? localValue.courseKey : localValue.microcastKey}
                  onChange={(id) => setLocalValue({
                    ...localValue,
                    ...(isCourse
                      ? { courseKey: id, microcastKey: '' }
                      : { courseKey: '', microcastKey: id }),
                    certificationID: '',
                  })}
                  label="Select Item"
                />
              </Stack>
            </Stack>
            {isCourse && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">Certification</Typography>
                {certifications.length > 0 ? (
                  <Select
                    value={localValue.certificationID || ''}
                    onChange={(ev) => setLocalValue({
                        ...localValue,
                        certificationID: ev.target.value,
                    })}
                    disabled={!localValue.courseKey || certifications.length === 0}
                    label="Select Certification"
                    fullWidth
                  >
                    {certifications.map((cert) => (
                      <MenuItem key={cert.value} value={cert.value}>
                        {cert.label}
                      </MenuItem>
                    ))}
                  </Select>
                )
              : (
                <Typography variant="subtitle2">No certifications available for this course.</Typography>
              )}
              </Stack>
            )}
            <Stack alignItems="center">
              <Button
                variant="contained"
                color="primary"
                onClick={handleAdd}
                disabled={!canAdd}
              >
                Add
              </Button>
            </Stack>
            <Stack spacing={2}>
              {(Array.isArray(value) ? value : []).map((prerequisite, index) => {
                const isLoaded = prerequisite.course?.syncState === 'loaded'
                  || prerequisite.microcast?.syncState === 'loaded';

                const courseKey = isLoaded
                  ? prerequisite.course?.id
                  : (prerequisite.course || prerequisite.courseID);

                const microcastKey = isLoaded
                  ? prerequisite.microcast?.id
                  : (prerequisite.microcast || prerequisite.microcastID);
                return (
                  <PrerequisiteItem
                    key={prerequisite.key || prerequisite.id}
                    prerequisiteType={prerequisite.prerequisiteType}
                    courseKey={courseKey}
                    microcastKey={microcastKey}
                    certificationID={prerequisite.certificationID}
                    onDelete={() => handleDelete(index)}
                  />
                );
              })}
            </Stack>
          </Stack>
        );
      }}
    />
  );
};

export default observer(CoursePrerequisiteField);
